import { Component, Input, OnInit, inject } from '@angular/core'
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { EditorModule } from '@tinymce/tinymce-angular'
import { ToastrService } from 'ngx-toastr'
import { SmartQuoteService } from 'src/app/core/services/smartquote/smartquote.service'
import { ButtonComponent } from 'src/app/shared/components/form-elements/button/button.component'
import { InputComponent } from 'src/app/shared/components/form-elements/input/input.component'
import { LabelComponent } from 'src/app/shared/components/form-elements/label/label.component'
import { SwitchComponent } from 'src/app/shared/components/form-elements/switch/switch.component'
import { CompanyEmailTemplate } from 'src/app/shared/interfaces/company/company-email-template'
import { SendSmartQuoteToCustomerRequest } from 'src/app/shared/interfaces/smartquote/send-smart-quote-to-customer-request.interface'
import { SmartQuote } from 'src/app/shared/interfaces/smartquote/smart-quote.interface'
import { SmartQuoteStore } from '../../../smartquote-edit-store.service'
import { tinymceSettings } from 'src/app/shared/tinymce-settings'
import { AppStore } from 'src/app/core/app.store'

interface SendSmartQuoteForm {
    includeTerms: FormControl<boolean>
    sendSMS: FormControl<boolean>
    setupChaser: FormControl<boolean>
    emailAddress: FormControl<string>
    subject: FormControl<string>
    heading: FormControl<string>
    subheading: FormControl<string>
    advert: FormControl<string>
    body: FormControl<string>
}

@Component({
    selector: 'sf-smartquote-email-modal',
    imports: [ReactiveFormsModule, EditorModule, ButtonComponent, InputComponent, SwitchComponent, LabelComponent],
    templateUrl: './smartquote-email-modal.component.html',
    styleUrl: './smartquote-email-modal.component.scss',
})
export class SmartquoteEmailModalComponent implements OnInit {
    activeModal = inject(NgbActiveModal)
    private fb = inject(FormBuilder)
    private smartQuoteService = inject(SmartQuoteService)
    private store = inject(SmartQuoteStore)
    private appStore = inject(AppStore)
    private toastr = inject(ToastrService)

    tinymceSettings = tinymceSettings

    emailTemplate: CompanyEmailTemplate | null = null

    form = this.fb.group<SendSmartQuoteForm>({
        includeTerms: this.fb.nonNullable.control<boolean>(true),
        sendSMS: this.fb.nonNullable.control<boolean>(this.appStore.companySettings()?.smartQuoteSendSms || false),
        setupChaser: this.fb.nonNullable.control<boolean>(
            this.appStore.companySettings()?.smartQuoteSetupChaser || false
        ),
        emailAddress: this.fb.nonNullable.control<string>('', [Validators.required, Validators.email]),
        subject: this.fb.nonNullable.control<string>('', [Validators.required]),
        heading: this.fb.nonNullable.control<string>(''),
        subheading: this.fb.nonNullable.control<string>(''),
        advert: this.fb.nonNullable.control<string>(''),
        body: this.fb.nonNullable.control<string>('', [Validators.required]),
    })

    private _smartQuote: SmartQuote | null = null
    @Input({ required: true }) smartQuoteId!: number

    @Input()
    set smartQuote(value: SmartQuote | null) {
        this._smartQuote = value
    }
    get smartQuote(): SmartQuote | null {
        return this._smartQuote
    }

    @Input() emailAddress!: string

    editorOptions: any = {
        toolbar: 'mergetags',
        menubar: false,
        plugins: 'lists link image table code help wordcount',
    }

    ngOnInit(): void {
        this.smartQuoteService.getTranslatedEmailForSmartQuote(this.smartQuoteId).subscribe((template) => {
            this.emailTemplate = template
            this.form.patchValue(template)
        })

        this.form.patchValue({ emailAddress: this.emailAddress })
    }

    sendToCustomer(): void {
        if (this.form.invalid) {
            this.toastr.error('Please fill in all required fields')
            return
        }

        const request = this.form.value as SendSmartQuoteToCustomerRequest

        this.smartQuoteService.sendSmartQuoteToCustomer(this.smartQuoteId, request).subscribe(() => {
            this.toastr.success('SmartQuote sent to customer')
            this.activeModal.close()
        })
    }
}
